import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { bundlesFirst } from '../lib/types';
import type { Banner, Category, Exam, Product } from '../lib/types';

type CatalogValue = {
  loading: boolean;
  offline: boolean;
  products: Product[];
  categories: Category[];
  exams: Exam[];
  banners: Banner[];
  getProduct: (id?: string) => Product | undefined;
  getProductsByType: (type: string) => Product[];
  getBestsellers: () => Product[];
  getFreeProducts: () => Product[];
};

const CatalogContext = createContext<CatalogValue | undefined>(undefined);

export function CatalogProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<{ products: Product[]; categories: Category[]; exams: Exam[]; banners: Banner[] }>({
    products: [],
    categories: [],
    exams: [],
    banners: [],
  });
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .bootstrap()
      .then((res) => {
        if (cancelled) return;
        setData({ ...res, products: bundlesFirst(res.products) });
        setOffline(false);
      })
      .catch(() => !cancelled && setOffline(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<CatalogValue>(() => {
    const { products } = data;
    return {
      loading,
      offline,
      ...data,
      getProduct: (id) => products.find((p) => p.id === id),
      getProductsByType: (type) => products.filter((p) => p.type === type),
      getBestsellers: () => products.filter((p) => p.bestseller),
      getFreeProducts: () => products.filter((p) => p.isFree),
    };
  }, [data, loading, offline]);

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error('useCatalog must be used within CatalogProvider');
  return ctx;
}
